import React from 'react';

import {
  Typography as MUITypography,
  TypographyProps as MUITypographyProps,
  useTheme,
} from '@mui/material';
import classNames from 'classnames';
import { get } from 'lodash';

import { Tokens } from '../Theme';
import type { DeepKeys } from '../types';

import { useStyles } from './Typography.styles';

export type TypographyColor =
  | DeepKeys<Tokens['colors']>
  | 'inherit'
  | undefined;

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle1'
  | 'subtitle2'
  | 'body1'
  | 'body2'
  | 'body3'
  | 'caption'
  | 'button'
  | 'overline'
  | 'inherit';

type TypographyProps = Pick<
  MUITypographyProps,
  'children' | 'ref' | 'className' | 'onClick' | 'sx'
> & {
  paragraph?: boolean;
  noWrap?: boolean;
  gutterBottom?: boolean;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  display?: 'block' | 'inline';
  variant?: TypographyVariant;
  color?: TypographyColor;
  component?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span';
};

export const variantMapping = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'div',
  body2: 'div',
};

/**
 * Компонент типографики, который расширяет компонент Material-UI Typography дополнительными стилями и опциями цвета.
 *
 * @param {TypographyProps} props - Свойства для компонента Typography.
 * @param {string} [props.variant='body1'] - Вариант стиля типографики.
 * @param {TypographyColor} [props.color='text.light.primary'] - Цвет текста. Может быть ключом из цветов темы или 'inherit'.
 * @param {string} [props.className] - Дополнительные CSS-классы для применения к компоненту типографики.
 * @param {React.ReactNode} props.children - Текст или элементы, которые будут отображаться внутри компонента типографики.
 * @returns {JSX.Element} - Отрендеренный компонент Typography.
 */

export const Typography = ({
  variant = 'body1',
  color = 'text.light.primary',
  className,
  ...rest
}: TypographyProps) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const body3 = classes.body3;

  return (
    <MUITypography
      {...rest}
      variant={variant !== 'body3' ? variant : 'body2'}
      color="inherit"
      variantMapping={variantMapping}
      classes={classes}
      className={classNames(className, {
        [body3]: variant === 'body3',
        [`aqa_${variant}`]: true,
      })}
      style={{
        color:
          color === 'inherit' ? 'inherit' : get(theme.tokens.colors, color),
      }}
    />
  );
};
